import React from 'react';
import { executeActionRequest } from '../../../services/trips/actionRequests';
import './chatCards.css';

// Trip-draft confirmation card (CONFIRM_TRIP flow in the v2 chat).
// Carries the T0.2 guard: creating without both dates is blocked with an
// inline message instead of crashing in date conversion.

export interface TripDraftShape {
  title?: string;
  destinations?: string[];
  dates?: { startDate?: string; endDate?: string } | null;
  budget?: { amount?: number; currency?: string } | null;
  description?: string;
}

interface TripDraftCardProps {
  draft: TripDraftShape;
  resolution?: 'confirmed' | 'dismissed' | 'failed';
  onResolve: (resolution: 'confirmed' | 'dismissed' | 'failed', note: string) => void;
}

const TripDraftCard: React.FC<TripDraftCardProps> = ({ draft, resolution, onResolve }) => {
  const [busy, setBusy] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const handleConfirm = async () => {
    if (busy) return;
    // T0.2 guard: dates must both exist before conversion/creation.
    if (!draft.destinations || draft.destinations.length === 0) {
      setValidationError('Add at least one destination before creating the trip.');
      return;
    }
    if (!draft.dates?.startDate || !draft.dates?.endDate) {
      setValidationError('Ask the assistant to set start and end dates first.');
      return;
    }
    setValidationError(null);
    setBusy(true);
    try {
      const note = await executeActionRequest({
        action: 'CREATE_TRIP',
        payload: draft as Record<string, any>,
      });
      onResolve('confirmed', note);
    } catch (error: any) {
      onResolve('failed', error?.message || 'The trip could not be created');
    } finally {
      setBusy(false);
    }
  };

  if (resolution) {
    return (
      <div className={`v2-chat-card__resolution v2-chat-card__resolution--${resolution}`}>
        {resolution === 'confirmed' && '✓ Trip created'}
        {resolution === 'dismissed' && '✕ Dismissed'}
        {resolution === 'failed' && '⚠ Failed — try again from the trips page'}
      </div>
    );
  }

  return (
    <div className="v2-chat-card" role="group" aria-label="Confirm trip draft">
      <p className="v2-chat-card__title">{draft.title || 'Your trip'}</p>
      <p className="v2-chat-card__summary">
        {(draft.destinations || []).join(' · ') || 'No destinations yet'}
        {draft.dates?.startDate && draft.dates?.endDate
          ? ` — ${draft.dates.startDate} → ${draft.dates.endDate}`
          : ' — dates not set'}
        {draft.budget?.amount ? ` — ${draft.budget.amount} ${draft.budget.currency || ''}` : ''}
      </p>
      {validationError && (
        <p className="v2-chat-card__error" role="alert">{validationError}</p>
      )}
      <div className="v2-chat-card__actions">
        <button type="button" className="v2-chat-card__confirm" onClick={handleConfirm} disabled={busy}>
          {busy ? 'Creating…' : 'Create trip'}
        </button>
        <button
          type="button"
          className="v2-chat-card__cancel"
          onClick={() => onResolve('dismissed', 'Dismissed')}
          disabled={busy}
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default TripDraftCard;
